import React, { useState, useEffect, useCallback } from 'react';
import AccountDropdown from './AccountDropdown';
import { apiFetch } from './api';
import './UsersPage.css';

export default function UsersPage({ onNavigate, currentUser, onLogout, onUserUpdated }) {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newEmail, setNewEmail]     = useState('');
  const [newName, setNewName]       = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingName, setEditingName]   = useState('');
  const [addError, setAddError]     = useState('');
  const [createdUser, setCreatedUser] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/users')
      .then(r => r.json())
      .then(data => { setUsers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setCreatedUser(null);
    const res = await apiFetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newEmail, name: newName }),
    });
    if (res.status === 201) {
      const data = await res.json();
      setCreatedUser(data);
      setNewEmail('');
      setNewName('');
      load();
    } else {
      const data = await res.json().catch(() => ({}));
      setAddError(data.detail || 'Failed to create user.');
    }
  };

  const handleSaveEdit = async () => {
    const res = await apiFetch(`/api/users/${encodeURIComponent(editingEmail)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingName }),
    });
    if (res.ok) {
      setEditingEmail(null);
      load();
      if (editingEmail === currentUser?.email) onUserUpdated?.();
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    await apiFetch(`/api/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
    load();
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', background: 'var(--eh-bg-page)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2 flex-shrink-0">
        <span
          className="navbar-brand mb-0 d-flex align-items-center gap-3"
          style={{ cursor: 'pointer' }}
          onClick={() => onNavigate('home')}
        >
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            User Management
          </span>
        </span>
        <AccountDropdown currentUser={currentUser} onLogout={onLogout} />
      </nav>

      <main className="flex-grow-1 overflow-auto p-4">
        <div className="row g-4">

          {/* Add user card */}
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header">Add User</div>
              <div className="card-body">
                {addError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.875rem' }}>{addError}</div>
                )}
                {createdUser && (
                  <div className="alert alert-success py-2 mb-3" style={{ fontSize: '0.875rem' }}>
                    <strong>{createdUser.email}</strong> created. Temporary password:&nbsp;
                    <code style={{ userSelect: 'all' }}>{createdUser.temporaryPassword}</code>
                    <span className="text-muted"> — share this with the user; it won't be shown again.</span>
                  </div>
                )}
                <form onSubmit={handleAdd} className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Name <span className="text-muted fw-normal">(optional)</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="col-auto">
                    <button type="submit" className="btn btn-primary">Add User</button>
                  </div>
                </form>
                <div className="form-text mt-2">
                  New users will be required to set a password on first login.
                </div>
              </div>
            </div>
          </div>

          {/* Users table card */}
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header">Users</div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="p-4 text-center text-muted">Loading&hellip;</div>
                ) : users.length === 0 ? (
                  <div className="p-4 text-center text-muted fst-italic">No users found.</div>
                ) : (
                  <table className="table table-hover users-table mb-0">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.email}>
                          <td className="align-middle">{user.email}</td>
                          <td className="align-middle users-name-cell">
                            {editingEmail === user.email ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editingName}
                                onChange={e => setEditingName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') setEditingEmail(null);
                                }}
                                autoFocus
                              />
                            ) : (
                              <span>{user.name || <em className="text-muted">—</em>}</span>
                            )}
                          </td>
                          <td className="align-middle">
                            {user.mustChangePassword && (
                              <span className="badge users-badge-pending">Must Change Password</span>
                            )}
                          </td>
                          <td className="align-middle text-end users-actions-cell">
                            {editingEmail === user.email ? (
                              <>
                                <button className="btn btn-sm btn-primary me-1" onClick={handleSaveEdit}>Save</button>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingEmail(null)}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  onClick={() => { setEditingEmail(user.email); setEditingName(user.name || ''); }}
                                >
                                  Edit
                                </button>
                                {user.email !== currentUser?.email && (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(user.email)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
